import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BatteryCharging,
  CircleAlert,
  Crosshair,
  MapPin,
  Pause,
  Play,
  Phone,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Radio,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Signal,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { LiveMap } from '@/components/maps/live-map';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { PageHeader } from '@/components/ui/page-header';
import { useToast } from '@/components/ui/toast';
import { api } from '@/services/api';
import { locationKey, locationTime, useLiveLocations } from '@/hooks/use-live-locations';

function coordinates(item) {
  const value = item.location?.coordinates ?? item.lastReliablePosition?.coordinates;
  if (Array.isArray(value) && value.length >= 2) return [Number(value[0]), Number(value[1])];
  const longitude = Number(item.longitude ?? item.lng ?? item.lastReliablePosition?.longitude);
  const latitude = Number(item.latitude ?? item.lat ?? item.lastReliablePosition?.latitude);
  return Number.isFinite(longitude) && Number.isFinite(latitude) ? [longitude, latitude] : null;
}

function statusFor(item) {
  const stage = item.connectivityStage ?? item.member?.connectivityStage ?? 'online';
  const rideStatus = item.rideStatus ?? item.member?.rideStatus ?? 'riding';
  if (rideStatus === 'sos' || rideStatus === 'emergency' || stage === 'emergency_active') return 'emergency';
  if (stage === 'emergency_suspected' || stage === 'reconnecting') return 'attention';
  if (stage === 'signal_weak' || rideStatus === 'offline') return 'offline';
  return 'online';
}

function roleFor(item) {
  return item.rideRole ?? item.member?.rideRole ?? 'member';
}

function batteryFor(item) {
  const value = Number(item.deviceHealth?.batteryPercent ?? item.member?.deviceHealth?.batteryPercent);
  return Number.isFinite(value) ? Math.round(value) : null;
}

function displayTime(value) {
  const timestamp = locationTime(value);
  if (!timestamp) return 'No signal time';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return new Date(timestamp).toLocaleString();
}

function statusTone(status) {
  if (status === 'emergency') return 'error';
  if (status === 'attention') return 'warning';
  if (status === 'online') return 'success';
  return 'neutral';
}

export function LiveMonitoringPage() {
  const { locations, snapshots, realtimeConnected, isLoading, isFetching, error, refetch } = useLiveLocations();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tripFilter, setTripFilter] = useState(searchParams.get('tripId') ?? '');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [replayIndex, setReplayIndex] = useState(null);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const toast = useToast();
  const replayActive = replayIndex != null && snapshots.length > 0;
  const displayedLocations = replayActive
    ? snapshots[Math.min(replayIndex, snapshots.length - 1)]?.locations ?? locations
    : locations;

  useEffect(() => {
    if (!replayPlaying || !snapshots.length) return undefined;
    const timer = window.setInterval(() => {
      setReplayIndex((current) => {
        const next = current == null ? 0 : current + 1;
        if (next >= snapshots.length) {
          setReplayPlaying(false);
          return snapshots.length - 1;
        }
        return next;
      });
    }, 900);
    return () => window.clearInterval(timer);
  }, [replayPlaying, snapshots.length]);

  const trips = useMemo(() => {
    const values = new Map();
    for (const item of displayedLocations) {
      const id = String(item.tripId ?? item.trip?.id ?? '');
      if (!id) continue;
      values.set(id, {
        id,
        name: item.trip?.name ?? `Convoy ${id.slice(-6)}`,
        destination: item.trip?.destination?.label,
      });
    }
    return [...values.values()].sort((first, second) => first.name.localeCompare(second.name));
  }, [displayedLocations]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...displayedLocations]
      .filter((item) => !tripFilter || String(item.tripId ?? item.trip?.id ?? '') === tripFilter)
      .filter((item) => !roleFilter || roleFor(item) === roleFilter)
      .filter((item) => !statusFilter || statusFor(item) === statusFilter)
      .filter((item) => {
        if (!query) return true;
        return `${item.member?.name ?? ''} ${item.member?.username ?? ''} ${item.trip?.name ?? ''}`
          .toLowerCase()
          .includes(query);
      })
      .sort((first, second) => {
        const riskOrder = { emergency: 0, attention: 1, offline: 2, online: 3 };
        return riskOrder[statusFor(first)] - riskOrder[statusFor(second)] || locationTime(second) - locationTime(first);
      });
  }, [displayedLocations, roleFilter, search, statusFilter, tripFilter]);

  useEffect(() => {
    const memberId = searchParams.get('memberId');
    const tripId = searchParams.get('tripId');
    if (tripId) setTripFilter(tripId);
    if (memberId && tripId) setSelectedKey(`${tripId}:${memberId}`);
  }, [searchParams]);

  useEffect(() => {
    if (selectedKey && !displayedLocations.some((item) => locationKey(item) === selectedKey)) setSelectedKey('');
  }, [displayedLocations, selectedKey]);

  const selected = displayedLocations.find((item) => locationKey(item) === selectedKey) ?? filtered[0];
  const selectedStatus = selected ? statusFor(selected) : '';
  const onlineCount = displayedLocations.filter((item) => statusFor(item) === 'online').length;
  const attentionCount = displayedLocations.filter((item) => ['attention', 'offline'].includes(statusFor(item))).length;
  const emergencyCount = displayedLocations.filter((item) => statusFor(item) === 'emergency').length;
  const monitoringGrid = leftPanelOpen && rightPanelOpen
    ? 'xl:grid-cols-[280px_minmax(420px,1fr)_320px]'
    : leftPanelOpen
      ? 'xl:grid-cols-[280px_minmax(420px,1fr)]'
      : rightPanelOpen
        ? 'xl:grid-cols-[minmax(420px,1fr)_320px]'
        : 'xl:grid-cols-1';

  const selectRider = (item) => {
    const key = locationKey(item);
    setSelectedKey(key);
    setSearchParams({
      tripId: String(item.tripId ?? item.trip?.id ?? ''),
      memberId: String(item.memberId ?? item.member?.id ?? ''),
    });
  };

  const requestSafeCheck = async () => {
    if (!selected) return;
    const tripId = selected.tripId ?? selected.trip?.id;
    const memberId = selected.memberId ?? selected.member?.id;
    try {
      await api(`/api/v1/admin/trips/${tripId}/members/${memberId}/safe-check`, { method: 'POST' });
      toast.show('Safety check sent', 'success');
    } catch (cause) {
      toast.show(cause.message, 'error', 'Unable to send');
    }
  };

  return <div className="workspace-page">
    <PageHeader
      eyebrow="Operations center"
      title="Live Monitoring"
      meta={<div className="flex flex-wrap gap-2">
        <Badge tone={realtimeConnected ? 'success' : 'warning'}>{realtimeConnected ? <Wifi size={11}/> : <WifiOff size={11}/>} {realtimeConnected ? 'Live' : 'Reconnecting'}</Badge>
        <Badge tone="success">{onlineCount} online</Badge>
        <Badge tone="warning">{attentionCount} attention</Badge>
        <Badge tone={emergencyCount ? 'error' : 'neutral'}>{emergencyCount} emergency</Badge>
      </div>}
      actions={<div className="flex gap-2"><Button variant="secondary" className="h-8 px-2.5" title={leftPanelOpen ? 'Hide rider list' : 'Show rider list'} onClick={() => setLeftPanelOpen((value) => !value)}>{leftPanelOpen ? <PanelLeftClose size={14}/> : <PanelLeftOpen size={14}/>}</Button><Button variant="secondary" className="h-8 px-2.5" title={rightPanelOpen ? 'Hide rider detail' : 'Show rider detail'} onClick={() => setRightPanelOpen((value) => !value)}>{rightPanelOpen ? <PanelRightClose size={14}/> : <PanelRightOpen size={14}/>}</Button><Button className="h-8 px-3 text-xs" variant={replayActive ? 'primary' : 'secondary'} onClick={() => { if (!snapshots.length) return; setReplayIndex(replayActive ? null : Math.max(0, snapshots.length - 1)); setReplayPlaying(false); }}>{replayActive ? 'Live view' : 'Replay'}</Button><Button variant="secondary" className="h-8 px-2.5" onClick={() => refetch()} disabled={isFetching}><RefreshCw size={14} className={isFetching ? 'animate-spin' : ''}/></Button></div>}
    />

    <div className={`workspace-fill grid overflow-hidden border border-white/8 bg-[#050b12] ${monitoringGrid}`}>
      {leftPanelOpen && <aside className="flex min-h-0 flex-col border-b border-white/8 xl:border-b-0 xl:border-r">
        <div className="space-y-2 border-b border-white/8 p-3">
          <div className="relative"><Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600"/><Input className="h-9 pl-8 text-xs" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search rider or convoy"/></div>
          <div className="grid grid-cols-2 gap-2">
            <Select className="h-9 text-xs" value={tripFilter} onChange={(event) => setTripFilter(event.target.value)}><option value="">All convoys</option>{trips.map((trip) => <option key={trip.id} value={trip.id}>{trip.name}</option>)}</Select>
            <Select className="h-9 text-xs" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">All status</option><option value="online">Online</option><option value="attention">Attention</option><option value="offline">Offline</option><option value="emergency">Emergency</option></Select>
          </div>
          <Select className="h-9 text-xs" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}><option value="">All roles</option><option value="leader">Road Captain</option><option value="member">Rider</option><option value="sweeper">Sweeper</option></Select>
        </div>
        <div className="scrollbar min-h-0 flex-1 overflow-y-auto">
          {filtered.map((item) => {
            const key = locationKey(item);
            const active = key === locationKey(selected);
            const status = statusFor(item);
            const battery = batteryFor(item);
            return <button key={key} type="button" onClick={() => selectRider(item)} className={`w-full border-b border-white/6 p-3 text-left transition ${active ? 'bg-cyan-300/[.06]' : 'hover:bg-white/[.025]'}`}>
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 grid size-9 shrink-0 place-items-center border ${status === 'emergency' ? 'border-rose-400/35 bg-rose-400/10 text-rose-300' : status === 'attention' ? 'border-amber-300/30 bg-amber-300/8 text-amber-200' : 'border-cyan-300/20 bg-cyan-300/[.06] text-cyan-200'}`}><Radio size={15}/></span>
                <span className="min-w-0 flex-1"><span className="flex items-center gap-2"><strong className="truncate text-xs text-slate-100">{item.member?.name ?? `Rider ${String(item.memberId).slice(-6)}`}</strong><Badge tone={statusTone(status)}>{status}</Badge></span><span className="mt-1 block truncate text-[10px] text-slate-500">{item.trip?.name ?? 'No convoy'} · {roleFor(item).replaceAll('_', ' ')}</span><span className="mt-2 flex gap-3 text-[9px] text-slate-600"><span>{Math.round(Number(item.speedKmh) || 0)} km/h</span><span>{battery == null ? 'Battery --' : `${battery}%${item.deviceHealth?.isCharging ? ' ⚡' : ''}`}</span><span>{displayTime(item)}</span></span></span>
              </div>
            </button>;
          })}
          {!filtered.length && <div className="grid min-h-52 place-items-center px-6 text-center"><div><Signal size={28} className="mx-auto text-slate-700"/><p className="mt-3 text-xs font-bold text-slate-400">{isLoading ? 'Loading rider signals' : 'No rider matches this view'}</p></div></div>}
        </div>
      </aside>}

      <main className="relative min-h-0 bg-[#040810]">
        <div className="absolute left-3 top-3 z-10 flex items-center gap-2 border border-cyan-300/20 bg-[#03070d]/88 px-3 py-2 text-[9px] font-black uppercase tracking-[.14em] text-cyan-200 backdrop-blur-xl"><Crosshair size={12}/>{filtered.length} rider signals</div>
        {error && <div className="absolute bottom-3 left-3 z-10 border border-rose-400/25 bg-[#12070b]/90 px-3 py-2 text-[10px] text-rose-200">{error.message}</div>}
        {replayActive && <div className="absolute bottom-3 left-1/2 z-20 flex w-[min(560px,calc(100%-24px))] -translate-x-1/2 items-center gap-3 border border-cyan-300/20 bg-[#03070d]/92 px-3 py-2 backdrop-blur-xl">
          <button type="button" onClick={() => setReplayPlaying((value) => !value)} className="grid size-8 place-items-center border border-cyan-300/25 text-cyan-200">{replayPlaying ? <Pause size={14}/> : <Play size={14}/>}</button>
          <input className="h-1 flex-1 accent-cyan-300" type="range" min="0" max={Math.max(0, snapshots.length - 1)} value={Math.min(replayIndex ?? 0, Math.max(0, snapshots.length - 1))} onChange={(event) => { setReplayPlaying(false); setReplayIndex(Number(event.target.value)); }}/>
          <span className="w-24 text-right font-mono text-[9px] text-cyan-200">{snapshots[replayIndex]?.capturedAt ? new Date(snapshots[replayIndex].capturedAt).toLocaleTimeString() : 'Replay'}</span>
        </div>}
        <LiveMap locations={filtered}/>
      </main>

      {rightPanelOpen && <aside className="scrollbar min-h-0 overflow-y-auto border-t border-white/8 p-4 xl:border-l xl:border-t-0">
        {!selected ? <div className="grid h-full min-h-80 place-items-center text-center"><div><MapPin size={30} className="mx-auto text-slate-700"/><p className="mt-3 text-sm font-bold text-slate-300">Select a rider</p></div></div> : <div className="space-y-4">
          <div className="border border-white/8 bg-white/[.025] p-4">
            <div className="flex items-start justify-between gap-3"><div><p className="text-[9px] font-black uppercase tracking-[.16em] text-cyan-300">Rider detail</p><h2 className="mt-1 text-lg font-black text-white">{selected.member?.name ?? 'Rider'}</h2><p className="text-[11px] text-slate-500">@{selected.member?.username ?? 'unknown'} · {roleFor(selected).replaceAll('_', ' ')}</p></div><Badge tone={statusTone(selectedStatus)}>{selectedStatus}</Badge></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Metric icon={Route} label="Speed" value={`${Math.round(Number(selected.speedKmh) || 0)} km/h`}/>
            <Metric icon={BatteryCharging} label="Battery" value={batteryFor(selected) == null ? '--' : `${batteryFor(selected)}%`}/>
            <Metric icon={Signal} label="Connection" value={String(selected.connectivityStage ?? 'online').replaceAll('_', ' ')}/>
            <Metric icon={Crosshair} label="Accuracy" value={selected.accuracyMeters ? `${Math.round(selected.accuracyMeters)} m` : '--'}/>
          </div>
          <InfoBlock label="Convoy" value={selected.trip?.name ?? 'No active convoy'} extra={selected.trip?.destination?.label}/>
          <InfoBlock label="Last reliable position" value={coordinates(selected) ? `${coordinates(selected)[1].toFixed(6)}, ${coordinates(selected)[0].toFixed(6)}` : 'Unavailable'} extra={displayTime(selected)}/>
          <InfoBlock label="Device health" value={selected.deviceHealth?.backgroundTracking === false ? 'Background tracking needs attention' : 'Tracking active'} extra={selected.deviceHealth?.networkState ? `Network: ${selected.deviceHealth.networkState}` : undefined}/>
          <div className="grid gap-2 pt-2">
            <Button onClick={() => void requestSafeCheck()}><ShieldCheck size={14}/>Send safe check</Button>
            <Button variant="secondary" disabled={!selected.member?.phoneNumber} onClick={() => { if (selected.member?.phoneNumber) window.location.href = `tel:${selected.member.phoneNumber}`; }}><Phone size={14}/>Call rider</Button>
          </div>
          {selectedStatus === 'emergency' && <div className="border border-rose-400/25 bg-rose-400/[.055] p-3 text-xs text-rose-200"><div className="flex items-center gap-2 font-bold"><CircleAlert size={15}/>Emergency active</div><p className="mt-1 text-[11px] text-rose-200/70">Use the last reliable position and contact the rider.</p></div>}
        </div>}
      </aside>}
    </div>
  </div>;
}

function Metric({ icon: Icon, label, value }) {
  return <div className="border border-white/8 bg-black/15 p-3"><Icon size={13} className="text-cyan-300"/><p className="mt-2 text-[8px] font-black uppercase tracking-[.14em] text-slate-600">{label}</p><p className="mt-1 truncate text-xs font-bold capitalize text-slate-200">{value}</p></div>;
}

function InfoBlock({ label, value, extra }) {
  return <div className="border-b border-white/7 pb-3"><p className="text-[8px] font-black uppercase tracking-[.15em] text-slate-600">{label}</p><p className="mt-1 text-xs font-semibold text-slate-200">{value}</p>{extra && <p className="mt-1 text-[10px] text-slate-500">{extra}</p>}</div>;
}
